import React from "react";

/**
 * StatusGlyph — small expressive shape that conveys the *meaning* of a status
 * at a glance, replacing the previous flat color-dot. Each glyph is a tight
 * inline SVG (default 12×12) and respects `currentColor` only where useful so
 * the shapes themselves carry the semantic palette.
 *
 * Map of glyphs:
 *  • Success (Complete / Machining Complete / Passed / Shipped) — emerald
 *    filled circle with bold white tick.
 *  • Idle / queued (Queued / Pending / Pending Inspection) — hollow grey
 *    dashed circle.
 *  • Active / in-flight (Ordered / Processing / Under Inspection) — hollow
 *    blue circle.
 *  • Half progress (Partially Received / Pickup Awaited) — amber circle with
 *    the right half filled.
 *  • Delivered — WhatsApp-style blue double tick.
 *  • In Setup — purple circle with a small wrench-like diagonal stroke.
 *  • Negotiating Rates — amber circle with two offset bars (handshake feel).
 *  • Shipment Call Raised — amber filled flag.
 *  • Failed — rose filled circle with white cross.
 *  • Rework Required — teal filled circle with white refresh arrow.
 */
export default function StatusGlyph({ status, size = 12, className = "" }) {
  const s = size;
  switch (status) {
    /* ── Success ───────────────────────────────────────────────────────── */
    case "Complete":
    case "Machining Complete":
    case "Passed":
    case "Shipped":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" className={className} aria-hidden="true">
          <circle cx="6" cy="6" r="5.5" fill="#10b981" />
          <path d="M3.4 6.2 L5.2 8 L8.6 4.4" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
    /* ── Idle / queued ─────────────────────────────────────────────────── */
    case "Queued":
    case "Pending":
    case "Pending Inspection":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" className={className} aria-hidden="true">
          <circle cx="6" cy="6" r="4.6" fill="none" stroke="#94a3b8" strokeWidth="1.3" strokeDasharray="2 1.6" />
        </svg>
      );
    /* ── Active / in-flight ────────────────────────────────────────────── */
    case "Ordered":
    case "Processing":
    case "Under Inspection":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" className={className} aria-hidden="true">
          <circle cx="6" cy="6" r="4.6" fill="none" stroke="#3b82f6" strokeWidth="1.7" />
        </svg>
      );
    /* ── Half-filled (mid-progress) ────────────────────────────────────── */
    case "Partially Received":
    case "Pickup Awaited":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" className={className} aria-hidden="true">
          <circle cx="6" cy="6" r="5" fill="none" stroke="#f59e0b" strokeWidth="1.4" />
          <path d="M6 1 A 5 5 0 0 1 6 11 Z" fill="#f59e0b" />
        </svg>
      );
    /* ── Delivered (WhatsApp-style double tick) ────────────────────────── */
    case "Delivered":
      return (
        <svg width={s + 4} height={s} viewBox="0 0 16 12" className={className} aria-hidden="true">
          <path d="M1 7 L4 10 L9 4" stroke="#3b82f6" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <path d="M6 7 L9 10 L14 3" stroke="#3b82f6" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
    /* ── In Setup (wrench-tilt inside circle) ──────────────────────────── */
    case "In Setup":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" className={className} aria-hidden="true">
          <circle cx="6" cy="6" r="5" fill="none" stroke="#a855f7" strokeWidth="1.4" />
          <path d="M3.6 8.4 L8.4 3.6" stroke="#a855f7" strokeWidth="1.4" strokeLinecap="round" />
          <circle cx="3.6" cy="8.4" r="0.8" fill="#a855f7" />
          <circle cx="8.4" cy="3.6" r="0.8" fill="#a855f7" />
        </svg>
      );
    /* ── Negotiating Rates (two offset bars — handshake feel) ──────────── */
    case "Negotiating Rates":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" className={className} aria-hidden="true">
          <circle cx="6" cy="6" r="5" fill="none" stroke="#f59e0b" strokeWidth="1.3" />
          <path d="M3 4.8 L6 4.8" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M6 7.2 L9 7.2" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      );
    /* ── Shipment Call Raised (filled flag on pole) ────────────────────── */
    case "Shipment Call Raised":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" className={className} aria-hidden="true">
          <path d="M3 1.5 L3 10.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M3 2.5 L9.5 2.5 L7.8 5 L9.5 7.5 L3 7.5 Z" fill="#f59e0b" />
        </svg>
      );
    /* ── Failed (filled circle + cross) ────────────────────────────────── */
    case "Failed":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" className={className} aria-hidden="true">
          <circle cx="6" cy="6" r="5.5" fill="#ef4444" />
          <path d="M3.8 3.8 L8.2 8.2 M8.2 3.8 L3.8 8.2" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    /* ── Rework Required (filled circle + refresh arrow) ───────────────── */
    case "Rework Required":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" className={className} aria-hidden="true">
          <circle cx="6" cy="6" r="5.5" fill="#14b8a6" />
          <path
            d="M3.5 6 A 2.5 2.5 0 1 1 6 8.5"
            stroke="white"
            strokeWidth="1.4"
            fill="none"
            strokeLinecap="round"
          />
          <path d="M3 4.6 L3.5 6.2 L5 5.6" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      );
    /* ── Fallback ──────────────────────────────────────────────────────── */
    default:
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" className={className} aria-hidden="true">
          <circle cx="6" cy="6" r="3" fill="#64748b" />
        </svg>
      );
  }
}
